/**
 * PermissionsContext.js - ACMW v3.0.0
 * Permission checking and scope management context
 *
 * Provides:
 * - usePermissions hook
 * - can() function - Check if user has permission
 * - hasAccessToTenant() - Check tenant access
 * - getUserScope() - Get user's accessible publishers/billers
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions debe ser usado dentro de un PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [scope, setScope] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Load permissions and scope when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('PermissionsContext - User authenticated, loading permissions...');
      loadPermissionsAndScope();
    } else {
      console.log('PermissionsContext - User not authenticated, clearing permissions');
      setPermissions([]);
      setScope(null);
      setIsLoading(false);
      setHasLoadedOnce(false);
    }
  }, [isAuthenticated, user]);

  /**
   * Load both permissions and scope
   * Wait for both to complete before setting isLoading to false
   */
  const loadPermissionsAndScope = async () => {
    console.log('PermissionsContext - Starting to load permissions and scope');
    setIsLoading(true);
    try {
      // Wait for both API calls
      const [permissionsData, scopeData] = await Promise.all([
        api.get('/permissions/me'),
        api.get(`/users/${user.id}/scope`)
      ]);

      // Set both states together
      if (permissionsData.data.success) {
        setPermissions(permissionsData.data.data.permissions || []);
      }
      if (scopeData.data.success) {
        setScope(scopeData.data.data);
      }

      console.log('PermissionsContext - Finished loading permissions and scope');
      setHasLoadedOnce(true);
    } catch (error) {
      console.error('Error loading permissions and scope:', error);
      setPermissions([]);
      setScope(null);
    } finally {
      setIsLoading(false);
      console.log('PermissionsContext - isLoading set to false');
    }
  };


  /**
   * Check if user has a specific permission
   * @param {string} module - Module name (e.g., 'lotes', 'notificaciones')
   * @param {string} action - Action (e.g., 'view', 'create', 'edit', 'delete', 'export')
   * @param {string|null} subModule - Optional sub-module (e.g., 'basico', 'avanzado')
   * @returns {boolean}
   */
  const can = (module, action, subModule = null) => {
    // Only Super Admin (role_id = 1) has all permissions automatically
    if (user?.role_id === 1) {
      return true;
    }

    // Check if permission exists in user's role permissions
    const permission = permissions.find(p =>
      p.module === module &&
      (subModule ? p.sub_module === subModule : p.sub_module === null)
    );

    if (!permission) {
      return false;
    }

    // Map action to permission field
    const actionMap = {
      view: 'can_view',
      create: 'can_create',
      edit: 'can_edit',
      delete: 'can_delete',
      export: 'can_export'
    };

    const permissionField = actionMap[action];
    // Convert to boolean because DB returns 0/1 (numbers) not true/false
    return Boolean(permission[permissionField]);
  };

  /**
   * Check if user can perform ANY action on a module
   * @param {string} module - Module name
   * @param {string|null} subModule - Optional sub-module
   * @returns {boolean}
   */
  const canAccessModule = (module, subModule = null) => {
    // Only Super Admin (role_id = 1) has all access automatically
    if (user?.role_id === 1) {
      return true;
    }

    const permission = permissions.find(p =>
      p.module === module &&
      (subModule ? p.sub_module === subModule : p.sub_module === null)
    );

    if (!permission) {
      console.log(`canAccessModule(${module}, ${subModule}): No permission found`);
      return false;
    }

    // Check if user has at least one permission
    // Convert to boolean explicitly because DB returns 0/1 (numbers) not true/false
    const hasAccess = Boolean(
      permission.can_view ||
      permission.can_create ||
      permission.can_edit ||
      permission.can_delete ||
      permission.can_export
    );

    console.log(`canAccessModule(${module}, ${subModule}):`, {
      permission,
      hasAccess,
      can_view: permission.can_view,
      can_create: permission.can_create
    });

    return hasAccess;
  };

  /**
   * Check if user has access to a specific tenant (publisher/biller)
   * @param {number|null} publisherId - Publisher ID
   * @param {string|null} billerId - Biller ID
   * @returns {boolean}
   */
  const hasAccessToTenant = (publisherId, billerId = null) => {
    // Only Super Admin (role_id = 1) has access to all tenants
    if (user?.role_id === 1) {
      return true;
    }

    if (!scope) {
      return false;
    }

    // Publisher users: check if publisherId matches
    if (user?.user_type === 'publisher') {
      return scope.publishers.some(p => p.id === publisherId);
    }

    // Biller users: check if they have access to this biller
    if (user?.user_type === 'biller') {
      if (billerId) {
        return scope.billers.some(b =>
          b.publisher_id === publisherId && b.biller_id === billerId
        );
      }
      // If only checking publisher, check if they have any biller under this publisher
      return scope.billers.some(b => b.publisher_id === publisherId);
    }

    return false;
  };

  /**
   * Get list of accessible publishers
   * @returns {Array}
   */
  const getAccessiblePublishers = () => {
    if (user?.user_type === 'platform') {
      // Platform users can access all - would need to fetch from API
      return [];
    }
    return scope?.publishers || [];
  };

  /**
   * Get list of accessible billers for a publisher
   * @param {number} publisherId
   * @returns {Array}
   */
  const getAccessibleBillers = (publisherId) => {
    if (user?.user_type === 'platform') {
      // Platform users can access all - would need to fetch from API
      return [];
    }

    if (!scope) {
      return [];
    }

    return scope.billers.filter(b => b.publisher_id === publisherId);
  };

  /**
   * Check if user has multiple permissions at once
   * @param {Array} checks - Array of {module, action, subModule?}
   * @returns {boolean}
   */
  const canAll = (checks) => {
    return checks.every(check => can(check.module, check.action, check.subModule));
  };

  /**
   * Check if user has at least one of multiple permissions
   * @param {Array} checks - Array of {module, action, subModule?}
   * @returns {boolean}
   */
  const canAny = (checks) => {
    return checks.some(check => can(check.module, check.action, check.subModule));
  };

  /**
   * Check if user has access to any sub-module of a module
   * Useful for modules with sub-modules (e.g., 'configuracion' with 'usuarios', 'inflacion')
   * @param {string} module - Module name
   * @returns {boolean}
   */
  const hasAnySubModuleAccess = (module) => {
    // Only Super Admin (role_id = 1) has all access automatically
    if (user?.role_id === 1) {
      return true;
    }

    // Find any permission for this module that has a sub_module
    const hasAccess = permissions.some(p => {
      if (p.module !== module || p.sub_module === null) {
        return false;
      }
      // Check if user has at least one permission on this sub-module
      return Boolean(
        p.can_view ||
        p.can_create ||
        p.can_edit ||
        p.can_delete ||
        p.can_export
      );
    });

    return hasAccess;
  };

  /**
   * Get user's role name
   * @returns {string|null}
   */
  const getUserRole = () => {
    return user?.role_name || null;
  };

  /**
   * Check if user is admin (Super Admin role only)
   * @returns {boolean}
   */
  const isAdmin = () => {
    // Only Super Admin (role_id = 1) is considered admin
    return user?.role_id === 1;
  };

  /**
   * Reload permissions (useful after role change)
   */
  const reloadPermissions = async () => {
    await loadPermissionsAndScope();
  };

  const value = {
    permissions,
    scope,
    isLoading,
    can,
    canAccessModule,
    canAll,
    canAny,
    hasAnySubModuleAccess,
    hasAccessToTenant,
    getAccessiblePublishers,
    getAccessibleBillers,
    getUserRole,
    isAdmin,
    reloadPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
